/**
 * src/lib/useApi.js — the data-fetching hook every page uses.
 *
 * Deliberately small: this app has no server-state library, and every page has
 * the same three needs — fetch on mount, show a loading state, show the
 * server's error message if it fails, and re-fetch on demand after a mutation.
 *
 * The `deps` array works like useEffect's, so `useApi('/api/projects?status=' + status, [status])`
 * re-fetches when the filter changes.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

import { request } from './api.js';

/**
 * Fetches a path and tracks loading/error state.
 *
 * @param {string|null} path Path to fetch, or null to skip (for dependent
 *   fetches where an id is not known yet).
 * @param {unknown[]} [deps] Values that should trigger a re-fetch.
 * @returns {{ data: any, error: string|null, loading: boolean, reload: () => void }}
 */
export function useApi(path, deps = []) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(Boolean(path));
  const [nonce, setNonce] = useState(0);

  // Guards against a slow first response overwriting a newer one when deps
  // change quickly (typing in the client search box, for instance).
  const latest = useRef(0);

  useEffect(() => {
    if (!path) {
      setData(null);
      setLoading(false);
      return undefined;
    }

    const requestId = latest.current + 1;
    latest.current = requestId;
    const controller = new AbortController();

    setLoading(true);
    setError(null);

    request(path, { signal: controller.signal })
      .then((res) => {
        if (latest.current === requestId) setData(res);
      })
      .catch((err) => {
        // An aborted request is not a failure; it was superseded.
        if (err.name === 'AbortError') return;
        if (latest.current === requestId) setError(err.message);
      })
      .finally(() => {
        if (latest.current === requestId) setLoading(false);
      });

    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, nonce, ...deps]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);

  /**
   * Re-fetch when the tab regains focus.
   *
   * Without this, a page fetched once on mount shows whatever was true when it
   * was opened, forever. That is actively misleading here: a client signs their
   * consent form in their own browser, the artist glances at the dashboard tab
   * they opened ten minutes ago, and it still says "pending". The data is
   * right; the screen is stale.
   *
   * `visibilitychange` rather than `focus` because switching tabs does not
   * always fire `focus`, and this is precisely the tab-switching case. Fetches
   * are skipped while hidden so a backgrounded dashboard is not polling.
   */
  useEffect(() => {
    if (!path) return undefined;
    const onVisible = () => {
      if (document.visibilityState === 'visible') setNonce((n) => n + 1);
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [path]);

  return { data, error, loading, reload };
}
